import type { GSplatVisualShowcaseEffectOptions } from './GSplatVisualShowcaseEffect'
import { GSplatVisualShowcaseEffect } from './GSplatVisualShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const flareShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform float uIntensity;
uniform vec3 uCenter;
uniform float uEffectScale;

${SHOWCASE_SHADER_COMMON_GLSL}

void modifySplatCenter(inout vec3 center) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float radius = length(local.xy);
  float front = fract(t * 0.18) * 5.5;
  float ring = showcaseRing(radius, front, 0.42);
  vec2 dir = normalize(local.xy + vec2(0.0001));

  center.xy += dir * ring * 0.22 * uIntensity * uEffectScale;
  center.z += ring * sin(t * 5.0 + radius * 4.0) * 0.12 * uIntensity * uEffectScale;
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  float t = uTime * uTimeScale;
  float radius = length((originalCenter - uCenter).xy / uEffectScale);
  float ring = showcaseRing(radius, fract(t * 0.18) * 5.5, 0.42);
  gsplatApplyUniformScale(covA, covB, 1.0 + ring * 0.65 * uIntensity);
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float radius = length(local.xy);
  float ring = showcaseRing(radius, fract(t * 0.18) * 5.5, 0.52);
  vec3 flare = mix(vec3(1.0, 0.28, 0.08), vec3(1.0, 0.92, 0.42), ring);
  color.rgb = mix(color.rgb, flare, showcaseSaturate(ring * 0.85 * uIntensity));
}
`

const flareShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = (*center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let radius = length(local.xy);
  let ring = showcaseRing(radius, fract(t * 0.18) * 5.5, 0.42);
  let direction = normalize(local.xy + vec2f(0.0001));
  let lateral = direction * ring * 0.22 * uniform.uIntensity * uniform.uEffectScale;
  let lift = ring * sin(t * 5.0 + radius * 4.0) *
    0.12 * uniform.uIntensity * uniform.uEffectScale;
  (*center) = vec3f((*center).x + lateral.x, (*center).y + lateral.y, (*center).z + lift);
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let t = uniform.uTime * uniform.uTimeScale;
  let radius = length((originalCenter - uniform.uCenter).xy / uniform.uEffectScale);
  let ring = showcaseRing(radius, fract(t * 0.18) * 5.5, 0.42);
  *scale = (*scale) * (1.0 + ring * 0.65 * uniform.uIntensity);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = (center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let radius = length(local.xy);
  let ring = showcaseRing(radius, fract(t * 0.18) * 5.5, 0.52);
  let flare = mix(vec3f(1.0, 0.28, 0.08), vec3f(1.0, 0.92, 0.42), ring);
  (*color) = vec4f(
    mix((*color).rgb, flare, showcaseSaturate(ring * 0.85 * uniform.uIntensity)),
    (*color).a
  );
}
`

export class GSplatVisualFlareEffect extends GSplatVisualShowcaseEffect {
  constructor(options: GSplatVisualShowcaseEffectOptions = {}) {
    super(
      {
        id: 'visual-flare',
        shader: flareShaderGLSL,
        shaderWGSL: flareShaderWGSL,
      },
      options
    )
  }
}
